import { Check, ArrowUpRight } from "lucide-react";
import { useLang } from "@/i18n/LangContext";
import { PRICES, waLink } from "@/data/site";
import { Kicker, Reveal } from "./Reveal";
import { WhatsAppIcon } from "./Icons";

const MAIN = ["intro", "quarterly", "semiannual", "annual"];

const Price = ({ planKey, t, featured }) => {
  const v = PRICES[planKey];
  if (v === null) {
    return <span className="big-num text-7xl">{t.pricing.free}</span>;
  }
  if (v === "request" || v === undefined) {
    return (
      <span className="flex flex-col" data-testid={`pricing-on-request-${planKey}`}>
        <span className="big-num text-5xl">{t.pricing.onRequest}</span>
      </span>
    );
  }
  return (
    <span className="flex items-start gap-1">
      {planKey === "pt" && <span className="font-mono text-xs uppercase mt-2 mr-1 opacity-70">{t.pricing.from}</span>}
      <span className={`font-mono text-sm font-semibold mt-2 ${featured ? "text-bone/80" : "text-ink/60"}`}>CHF</span>
      <span className="big-num text-7xl">{v}</span>
      <span className={`font-mono text-sm mt-2 ${featured ? "text-bone/80" : "text-ink/60"}`}>*</span>
    </span>
  );
};

export const Pricing = () => {
  const { t } = useLang();
  const P = t.pricing.plans;

  return (
    <section id="prezzi" data-testid="pricing-section" className="relative bg-ink text-bone overflow-hidden">
      <div className="grain-layer" />
      <div className="absolute top-0 inset-x-0 h-3 diag-stripes opacity-90" />
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-28">
        <div className="grid lg:grid-cols-12 gap-8 items-end mb-16">
          <Reveal className="lg:col-span-8">
            <Kicker light testid="pricing-kicker">{t.pricing.kicker}</Kicker>
            <h2 data-testid="pricing-title" className="font-display text-6xl sm:text-7xl lg:text-8xl mt-5 leading-[0.85]">
              {t.pricing.title}
            </h2>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-4">
            <p className="text-bone/70 leading-relaxed" data-testid="pricing-subtitle">{t.pricing.sub}</p>
          </Reveal>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-6">
          {MAIN.map((k, i) => {
            const plan = P[k];
            const featured = k === "annual";
            return (
              <Reveal key={k} delay={i * 0.07}>
                <article
                  data-testid={`pricing-plan-${k}`}
                  className={`ticket h-full flex flex-col p-6 ${featured ? "ticket-featured lg:-translate-y-4" : ""}`}
                  style={{ "--notch": "178px" }}
                >
                  {featured && (
                    <span className="absolute -top-4 right-4 bg-bone text-ink font-mono text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 border-2 border-ink rotate-2">
                      {t.pricing.popular}
                    </span>
                  )}
                  <div className="flex items-center justify-between font-mono text-[11px] uppercase tracking-[0.2em] opacity-70">
                    <span>N.0{i + 1}</span>
                    <span>{plan.period}</span>
                  </div>
                  <h3 className="font-display text-4xl mt-3 leading-none">{plan.name}</h3>
                  <p className={`text-sm mt-2 min-h-[2.5rem] ${featured ? "text-bone/85" : "text-ink/65"}`}>{plan.desc}</p>
                  <div className="mt-4 h-[76px] flex items-end">
                    <Price planKey={k} t={t} featured={featured} />
                  </div>
                  <div className="perforation my-6" />
                  <ul className="space-y-2.5 text-sm flex-1">
                    {plan.features.map((f) => (
                      <li key={f} className="flex gap-2.5">
                        <Check className={`w-4 h-4 mt-0.5 shrink-0 ${featured ? "text-bone" : "text-hammer-red"}`} strokeWidth={3} />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <a
                    href={k === "intro" ? "#prova" : waLink(`${t.wa.prefill} (${plan.name})`)}
                    target={k === "intro" ? undefined : "_blank"}
                    rel="noopener noreferrer"
                    data-testid={`pricing-cta-${k}`}
                    className={`mt-7 btn-brutal w-full !text-lg ${featured ? "btn-bone" : k === "intro" ? "btn-red" : "btn-ink"}`}
                  >
                    {k === "intro" ? t.pricing.cta : t.pricing.ctaInfo}
                    {k === "intro" ? <ArrowUpRight className="w-4 h-4" /> : <WhatsAppIcon className="w-4 h-4" />}
                  </a>
                </article>
              </Reveal>
            );
          })}
        </div>

        {/* secondary row */}
        <div className="mt-14 grid lg:grid-cols-12 gap-8 lg:gap-6">
          {["pt", "dropin"].map((k, i) => {
            const plan = P[k];
            return (
              <Reveal key={k} delay={i * 0.07} className="lg:col-span-5">
                <article
                  data-testid={`pricing-plan-${k}`}
                  className="h-full border-2 border-bone/30 hover:border-hammer-red transition-colors p-6 grid sm:grid-cols-[1fr_auto] gap-6 items-center bg-coal"
                >
                  <div>
                    <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-bone/50">
                      N.0{i + 5} · {plan.period}
                    </div>
                    <h3 className="font-display text-4xl mt-2 leading-none">{plan.name}</h3>
                    <p className="text-sm text-bone/70 mt-2">{plan.desc}</p>
                    <ul className="mt-3 space-y-1 text-sm text-bone/80">
                      {plan.features.map((f) => (
                        <li key={f} className="flex gap-2">
                          <Check className="w-4 h-4 mt-0.5 text-hammer-red shrink-0" strokeWidth={3} /> {f}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="text-bone">
                    <Price planKey={k} t={t} featured />
                  </div>
                </article>
              </Reveal>
            );
          })}

          <Reveal delay={0.15} className="lg:col-span-2 flex items-center justify-center py-4">
            <div
              data-testid="pricing-student-discount"
              className="sticker w-44 h-44 lg:w-40 lg:h-40 bg-hammer-red text-bone border-[3px] border-bone rotate-[-10deg] shadow-[6px_6px_0_#F4EFE6] hover:rotate-[4deg] transition-transform duration-300"
            >
              <div className="px-3">
                <div className="big-num text-6xl">-20%</div>
                <div className="font-mono text-[10px] uppercase tracking-[0.15em] mt-1 leading-tight">{t.pricing.student}</div>
              </div>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1} className="mt-12 flex flex-col md:flex-row md:items-center justify-between gap-4 border-t-2 border-bone/15 pt-6">
          <p className="font-display text-2xl text-bone">
            <span className="text-hammer-red">{t.pricing.student}:</span> {t.pricing.studentText}
          </p>
          <p className="font-mono text-[11px] text-bone/50 max-w-md" data-testid="pricing-note">{t.pricing.note}</p>
        </Reveal>
      </div>
    </section>
  );
};
